import { ChangeDetectionStrategy, Component, ElementRef, inject, signal, Signal, viewChild, WritableSignal, OnInit, forwardRef, HostBinding, InputSignal, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteOrigin } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { LocationService } from '@core/services/location/locations.service';
import { IntersectionObserverDirective } from '@shared/directives/intersection-observer/intersection-observer.directive';
import { ObserveDirective } from '@shared/directives/intersection-observer/observe.directive';
import { Country } from '@shared/services/country/types/country.type';
import { ApiCollectionResponse } from '@shared/types/api/api-collection-response';
import { isEqual, isNull, isString } from 'es-toolkit';
import { BehaviorSubject, debounceTime, distinctUntilChanged, throttleTime } from 'rxjs';
import { CountrySelectChange } from './types/country-select-change.type';
import { CountrySelectTouch } from './types/country-select-touch.type';
import { MatFormFieldControl } from '@angular/material/form-field';
import { TranslationStoreService } from '@ogs-gmbh/ngx-translate';
import { CustomFormFieldBase } from '@shared/types/custom-form-field-base.type';

const CONTROL_TYPE: string = "country-select";

@Component({
  selector: 'app-country-select',
  standalone: true,
  imports: [ MatInputModule, ReactiveFormsModule, MatAutocompleteModule, IntersectionObserverDirective, ObserveDirective ],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: forwardRef(() => CountrySelectComponent)
    }
  ],
  templateUrl: './country-select.component.html',
  styleUrl: './country-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CountrySelectComponent extends CustomFormFieldBase<Country | null> implements ControlValueAccessor, MatFormFieldControl<Country | null>, OnInit {
  private readonly _translationStoreService: TranslationStoreService = inject(TranslationStoreService);

  public readonly controlType: string = CONTROL_TYPE;

  // TODO dont require access connected within component
  public readonly connectedFormField: InputSignal<MatAutocompleteOrigin> = input.required();

  private readonly _locationService: LocationService = inject(LocationService);

  private readonly _inputElement: Signal<ElementRef<HTMLInputElement>> = viewChild.required("countryInput");

  private readonly _searchTermChange$: BehaviorSubject<string> = new BehaviorSubject<string>("");

  // eslint-disable-next-line @unicorn/no-useless-undefined
  protected readonly selectableCountries: WritableSignal<ApiCollectionResponse<Country> | undefined> = signal(undefined);


  public static nextId: number = 0;

  @HostBinding() id: string = `${ CONTROL_TYPE }-${ CountrySelectComponent.nextId++ }`;


  private _touched: boolean = false;

  private _focused: boolean = false;

  public get focused (): boolean {
    return this._focused;
  }


  @HostBinding('class.floating')
  public get shouldLabelFloat (): boolean {
    return this.focused || !this.empty;
  }


  autofilled?: boolean;

  userAriaDescribedBy?: string;

  disableAutomaticLabeling?: boolean;

  private _onChange: CountrySelectChange = undefined;

  private _onTouch: CountrySelectTouch = undefined;


  constructor () {
    super();
    this.formControl = new FormControl<Country | null>(null);

    if (this.ngControl)
      this.ngControl.valueAccessor = this;

    this.formControl.valueChanges
      .pipe(
        takeUntilDestroyed()
      )
      .subscribe((value: Country | null) => {
        this._stateChange$.next();

        if (this._onChange)
          this._onChange(value);
      });
    this._searchTermChange$.pipe(
      takeUntilDestroyed(),
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(() => {
      this._stateChange$.next();
      this.loadCountries(true);
    });
    this._translationStoreService.getLocale$()
      .pipe(
        throttleTime(1000, undefined, { leading: true, trailing: true }),
        distinctUntilChanged(isEqual),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.loadCountries(true);
      });
  }

  public setDescribedByIds (ids: string[]): void {
    this._inputElement().nativeElement.setAttribute('aria-describedby', ids.join(' '));
  }

  public onContainerClick (mouseEvent: MouseEvent): void {
    if ((mouseEvent.target as Element).tagName.toLowerCase() !== 'input')
      this._inputElement().nativeElement.focus();
  }

  public ngOnInit (): void {
    this.loadCountries();
  }


  protected searchCountry (): void {
    const searchTerm: string = this._inputElement().nativeElement.value;

    this._searchTermChange$.next(searchTerm);
  }


  // eslint-disable-next-line @tseslint/class-methods-use-this
  protected displayCountry = (country: Country | string | null): string => (isString(country) || isNull(country)
    ? country ?? ""
    : country.name);


  protected loadCountries (reset?: boolean): void {
    const selectableCountries: ApiCollectionResponse<Country> | undefined = this.selectableCountries();

    if (reset || selectableCountries === undefined || selectableCountries.totalCount > selectableCountries.data.length) {
      this._locationService.getCountries({
        offset: reset || !selectableCountries?.data.length ? 0 : selectableCountries.data.length,
        searchTerm: this._searchTermChange$.getValue() === "" ? undefined : this._searchTermChange$.getValue()
      })
        .subscribe((response: ApiCollectionResponse<Country>) => {
          this.selectableCountries.set({
            totalCount: response.totalCount,
            data: reset ? response.data : [ ...selectableCountries?.data ?? [], ...response.data ]
          });
        });
    }
  }

  protected setFocusIn (): void {
    if (!this.focused) {
      this._focused = true;
      this._stateChange$.next();
    }
  }

  protected setFocusOut (focusEvent: FocusEvent): void {
    this._searchTermChange$.next("");

    if (!this._inputElement().nativeElement.contains(focusEvent.relatedTarget as Element)) {
      this._focused = false;
      this.markTouched();
      this._stateChange$.next();
    }
  }

  protected markTouched (): void {
    this._touched = true;

    if (this._onTouch)
      this._onTouch();
  }

  public registerOnChange (callback: CountrySelectChange): void {
    this._onChange = callback;
  }

  registerOnTouched (callback: CountrySelectTouch): void {
    this._onTouch = callback;
  }
}
